import React, { createContext, useState, useEffect, useMemo } from 'react';
import { reportStaticTextsService } from '../services/reportStaticTextsService';
import { toast } from 'react-toastify';

export const ReportStaticTextsContext = createContext();

export const ReportStaticTextsProvider = ({ children }) => {
  const [allTexts, setAllTexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'create'
  const [filters, setFilters] = useState({
    search: '',
    module: '',
  });
  
  // Load all texts once
  const loadTexts = async () => {
    setLoading(true);
    try {
      // Fetch all texts with a high limit to get everything
      const response = await reportStaticTextsService.getReportStaticTexts(1, 1000);
      if (response.data && response.data.success) {
        // Handle both array and paginated response formats
        let textsData = [];
        if (Array.isArray(response.data.data)) {
          textsData = response.data.data;
        } else if (response.data.data && Array.isArray(response.data.data.items)) {
          textsData = response.data.data.items;
        } else if (response.data.data && Array.isArray(response.data.data.data)) {
          textsData = response.data.data.data;
        }
        
        setAllTexts(textsData);
        return textsData;
      }
      return [];
    } catch (error) {
      console.error('Error loading report static texts:', error);
      toast.error('Failed to load report static texts');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load texts on component mount (only if user is authenticated)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadTexts();
    }
  }, []);

  // Update filters without making API calls
  const updateFilters = (newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  };

  // Create a new text
  const createText = async (textData) => {
    setLoading(true);
    try {
      const response = await reportStaticTextsService.createReportStaticText(textData);
      if (response.data && response.data.success) {
        toast.success('Report static text created successfully');
        await loadTexts(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error creating report static text:', error);
      toast.error('Failed to create report static text');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing text
  const updateText = async (textId, textData) => {
    setLoading(true);
    try {
      const response = await reportStaticTextsService.updateReportStaticText(textId, textData);
      if (response.data && response.data.success) {
        toast.success('Report static text updated successfully');
        await loadTexts(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error updating report static text:', error);
      toast.error('Failed to update report static text');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a text
  const deleteText = async (textId) => {
    setLoading(true);
    try {
      const response = await reportStaticTextsService.deleteReportStaticText(textId);
      if (response.data && response.data.success) {
        toast.success('Report static text deleted successfully');
        await loadTexts(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error deleting report static text:', error);
      toast.error('Failed to delete report static text');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Open modal for editing
  const openEditModal = (text) => {
    setSelectedText(text);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // Open modal for creating
  const openCreateModal = () => {
    setSelectedText(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedText(null);
  };

  // Filter and sort texts locally based on current filters
  const filteredTexts = useMemo(() => {
    // First filter the texts
    const filtered = allTexts.filter(text => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          (text.module_name && text.module_name.toLowerCase().includes(searchTerm)) || 
          (text.report_name && text.report_name.toLowerCase().includes(searchTerm));
        
        if (!matchesSearch) return false;
      }
      
      // Module filter
      if (filters.module && filters.module !== '') {
        if (!text.module_name || text.module_name.toLowerCase() !== filters.module.toLowerCase()) return false;
      }
      
      return true;
    });
    
    // Sort by module name and report name
    return filtered.sort((a, b) => {
      const moduleA = (a.module_name || '').toLowerCase();
      const moduleB = (b.module_name || '').toLowerCase();
      
      if (moduleA !== moduleB) {
        return moduleA.localeCompare(moduleB);
      }
      
      const reportA = (a.report_name || '').toLowerCase();
      const reportB = (b.report_name || '').toLowerCase();
      
      return reportA.localeCompare(reportB);
    });
  }, [allTexts, filters]);

  // Get unique modules
  const modules = useMemo(() => {
    const moduleSet = new Set();
    allTexts.forEach(text => {
      if (text.module_name) {
        moduleSet.add(text.module_name);
      }
    });
    return Array.from(moduleSet).sort();
  }, [allTexts]);

  return (
    <ReportStaticTextsContext.Provider value={{ 
      texts: filteredTexts,
      filteredTexts,
      allTexts,
      selectedText,
      isModalOpen,
      modalMode,
      loading,
      filters,
      modules,
      loadTexts,
      updateFilters,
      createText,
      updateText,
      deleteText,
      openEditModal,
      openCreateModal,
      closeModal
    }}>
      {children}
    </ReportStaticTextsContext.Provider>
  );
};
